import { applyAffiliationRim } from "./affiliation-rim";
import { parseHexRgba, tintRgba } from "./color-utils";
import type {
  AffiliationStyle,
  ArtPalette,
  BakeResult,
  SpriteTemplate,
} from "./types";

/**
 * Pure pixel bake — no DOM/Phaser. Grid chars → RGBA buffer + optional rim.
 */
export function bakePixels(
  template: SpriteTemplate,
  palette: ArtPalette,
  affiliation: AffiliationStyle | undefined,
  transparentKey = ".",
): BakeResult {
  const size = template.size;
  if (template.rows.length !== size) {
    throw new Error(
      `Sprite ${template.id}: expected ${size} rows, got ${template.rows.length}`,
    );
  }

  const rgba = new Uint8ClampedArray(size * size * 4);
  for (let y = 0; y < size; y++) {
    const row = template.rows[y]!;
    if (row.length !== size) {
      throw new Error(
        `Sprite ${template.id} row ${y}: expected ${size} cols, got ${row.length}`,
      );
    }
    for (let x = 0; x < size; x++) {
      const ch = row[x]!;
      const o = (y * size + x) * 4;
      if (ch === transparentKey) {
        rgba[o + 3] = 0;
        continue;
      }
      const remapHex = affiliation?.fillRemap?.[ch];
      const hex = remapHex ?? palette.colors[ch];
      if (!hex) {
        throw new Error(`Sprite ${template.id}: unknown palette key '${ch}'`);
      }
      let c = parseHexRgba(hex);
      // fillRemap already sets material color — skip multiply tint on remapped keys
      // so variants keep distinct fills (tint on purple chitin reads rim-only).
      if (!remapHex) c = tintRgba(c, affiliation?.bodyTint);
      rgba[o] = c.r;
      rgba[o + 1] = c.g;
      rgba[o + 2] = c.b;
      rgba[o + 3] = c.a;
    }
  }

  const base: BakeResult = { width: size, height: size, rgba };
  if (!affiliation?.rim) return base;
  return applyAffiliationRim(base, affiliation.rim);
}

/** Opaque mask for silhouette distance checks. */
export function silhouetteMask(
  template: SpriteTemplate,
  transparentKey = ".",
): boolean[] {
  const size = template.size;
  const mask: boolean[] = [];
  for (let y = 0; y < size; y++) {
    const row = template.rows[y]!;
    for (let x = 0; x < size; x++) {
      mask.push(row[x] !== transparentKey);
    }
  }
  return mask;
}

export function hammingDistance(a: boolean[], b: boolean[]): number {
  const n = Math.min(a.length, b.length);
  let d = 0;
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) d++;
  d += Math.abs(a.length - b.length);
  return d;
}
